import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMyInvoicesQuery, useStartDuesPaymentMutation } from "../../billing/api/billingApi";
import { isOutstandingDuesInvoice } from "../../billing/constants";
import { submitOnePayForm } from "../../logbook/utils/onepay";

const naira = (kobo?: number | null) =>
  "₦" + Number((kobo ?? 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Shown instead of a dead-end when a member is blocked by outstanding dues.
 * Lists the invoices that count against their standing and clears them ALL in a
 * single OnePay transaction. After paying they return and can apply again.
 */
export default function OutstandingDuesModal({
  open,
  onClose,
  balanceKobo,
  reason,
}: {
  open: boolean;
  onClose: () => void;
  balanceKobo?: number | null;
  reason?: string | null;
}) {
  const { data: invoices = [], isLoading, isError, refetch } = useMyInvoicesQuery(undefined, {
    skip: !open,
  });
  const [startDues, { isLoading: paying }] = useStartDuesPaymentMutation();
  const [err, setErr] = useState<string | null>(null);

  // Only invoices that count toward "financial standing". The rule lives in one
  // place — this list was inline and had already gone stale, so induction and
  // event invoices would have been shown as dues the API does not count.
  const outstanding = useMemo(
    () => (invoices || []).filter(isOutstandingDuesInvoice),
    [invoices],
  );

  const totalKobo = useMemo(
    () => balanceKobo ?? outstanding.reduce((s: number, inv: any) => s + Number(inv.balance || 0), 0),
    [balanceKobo, outstanding],
  );

  const payAll = async () => {
    setErr(null);
    try {
      const res: any = await startDues().unwrap();
      if (!res?.payment_url) throw new Error(res?.message || "Could not start the payment.");
      submitOnePayForm(res); // redirects to OnePay for the full amount
    } catch (e: any) {
      setErr(e?.data?.message || e?.message || "Could not start the payment.");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white border shadow-lg p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Clear your outstanding dues</h2>
            <p className="text-sm text-gray-500 mt-1">
              {reason ||
                "You have outstanding dues. Clear them to continue with your exam application."}
            </p>
          </div>
          <button onClick={onClose} className="px-2 py-1 rounded border hover:bg-gray-50">
            ✕
          </button>
        </div>

        {err ? (
          <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded p-3">{err}</div>
        ) : null}

        {/* Breakdown (read-only) */}
        <div className="mt-4 max-h-[280px] overflow-y-auto pr-1 space-y-2">
          {isLoading ? (
            <div className="text-sm text-gray-500">Loading your invoices…</div>
          ) : isError ? (
            <div className="text-sm text-red-600">
              Couldn't load your invoices.{" "}
              <button onClick={() => refetch()} className="underline">
                retry
              </button>
            </div>
          ) : outstanding.length === 0 ? (
            <div className="text-sm text-gray-600">
              No payable invoices were found here. You can review everything on your{" "}
              <Link to="/my-invoices" onClick={onClose} className="underline font-semibold">
                invoices page
              </Link>
              .
            </div>
          ) : (
            outstanding.map((inv: any) => (
              <div key={inv.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-800 truncate">
                    {inv.invoice_number ?? inv.source ?? "Invoice"}
                  </div>
                  <div className="text-xs text-gray-500">{inv.source ?? "dues"} • {inv.status}</div>
                </div>
                <div className="text-sm font-semibold text-gray-800 shrink-0">{naira(inv.balance)}</div>
              </div>
            ))
          )}
        </div>

        {/* Total + pay all */}
        <div className="mt-4 flex items-center justify-between border-t pt-3">
          <div className="text-sm text-gray-600">
            Total outstanding: <span className="font-bold text-gray-900">{naira(totalKobo)}</span>
          </div>
          <button
            onClick={payAll}
            disabled={paying || outstanding.length === 0}
            className="px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 disabled:opacity-50"
          >
            {paying ? "Starting payment…" : `Pay all (${naira(totalKobo)})`}
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <Link to="/my-invoices" onClick={onClose} className="text-sm text-gray-600 hover:underline">
            Open full invoices page
          </Link>
          <button onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
